import { useEffect, useState, useRef } from "react";
import {
  NETWORK_PING_URL,
  NETWORK_PING_INTERVAL,
  NETWORK_PING_TIMEOUT,
  NETWORK_THRESHOLDS,
  NETWORK_FAIL_LIMIT,
  NETWORK_STATUS_CONFIRMATION_COUNT,
} from "../components/VideoStream/constants";
import logger from "utils/logger";

export function useNetworkPing({
  url = NETWORK_PING_URL,
  interval = NETWORK_PING_INTERVAL,
  timeout = NETWORK_PING_TIMEOUT,
  failLimit = NETWORK_FAIL_LIMIT,
  statusConfirmationCount = NETWORK_STATUS_CONFIRMATION_COUNT,
} = {}) {
  const [status, setStatus] = useState("good");
  const stableStatus = useRef("good");
  const lastChangeTime = useRef(Date.now());
  const failedPings = useRef(0);
  const statusCount = useRef(0);
  const lastDetectedStatus = useRef("good");

  useEffect(() => {
    const handleOffline = () => {
      stableStatus.current = "disconnected";
      lastChangeTime.current = Date.now();
      setStatus("disconnected");
      failedPings.current = 0;
      statusCount.current = 0;
      lastDetectedStatus.current = "disconnected";
    };
    const handleOnline = () => {
      lastChangeTime.current = Date.now();
      statusCount.current = 0;
    };
    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, []);

  const smoothSetStatus = (newStatus) => {
    const now = Date.now();

    if (newStatus !== lastDetectedStatus.current) {
      lastDetectedStatus.current = newStatus;
      statusCount.current = 1;
      logger.info(`Status changed to ${newStatus}, count: 1`);
      return;
    }

    statusCount.current++;
    logger.info(`${newStatus} confirmed ${statusCount.current} times`);

    if (statusCount.current < statusConfirmationCount) {
      return;
    }

    if (newStatus !== stableStatus.current) {
      lastChangeTime.current = now;
      stableStatus.current = newStatus;
      logger.info(`Status CONFIRMED and CHANGED to: ${newStatus}`);
      setStatus(newStatus);
    } else {
      logger.info(`Status CONFIRMED (continuing): ${newStatus}`);
      setStatus(newStatus);
    }
  };

  useEffect(() => {
    const doPing = async () => {
      if (navigator.onLine === false) {
        smoothSetStatus("disconnected");
        failedPings.current = 0;
        return;
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);
      const start = performance.now();

      try {
        const res = await fetch(url, { method: "GET",mode: 'cors', cache: "no-store", signal: controller.signal });
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error("Bad response");
        const latency = performance.now() - start;
        failedPings.current = 0;
        if (latency > NETWORK_THRESHOLDS.POOR) {
          smoothSetStatus("poor");
        } else if (latency > NETWORK_THRESHOLDS.WEAK) {
          smoothSetStatus("weak");
        } else {
          smoothSetStatus("good");
        }
      } catch (err) {
        clearTimeout(timeoutId);
        failedPings.current++;
        if (failedPings.current >= failLimit) {
          if (navigator.onLine === false || err?.name === "TypeError") {
            smoothSetStatus("disconnected");
            return;
          }
          smoothSetStatus("poor");
          return;
        }
        smoothSetStatus("poor");
      }
    };
    const intervalId = setInterval(doPing, interval);
    doPing();
    return () => clearInterval(intervalId);
  }, [url, interval, timeout, failLimit]);

  return status;
}
